import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 8,
  columns = 5,
  showHeader = true,
}) => {
  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        {showHeader && (
          <TableHeader>
            <TableRow>
              {Array.from({ length: columns }).map((_, i) => (
                <TableHead key={i}>
                  <Skeleton className="h-4 w-24" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton className="h-4 w-full max-w-[120px]" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const CandidateTableSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      {/* Control Bar Skeleton */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search Bar Skeleton */}
        <div className="flex-1 max-w-md h-10 bg-muted animate-pulse rounded-md" />
        
        {/* Ideal Profile Selector Skeleton */}
        <div className="w-64 h-10 bg-muted animate-pulse rounded-md" />
      </div>

      {/* Table Section */}
      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 border-b border-border">
                <TableHead className="px-4 py-3">
                  <Skeleton className="h-3 w-12" />
                </TableHead>
                <TableHead className="px-4 py-3 text-center">
                  <Skeleton className="h-3 w-20 mx-auto" />
                </TableHead>
                <TableHead className="px-4 py-3 text-center">
                  <Skeleton className="h-3 w-16 mx-auto" />
                </TableHead>
                <TableHead className="px-4 py-3 text-center">
                  <Skeleton className="h-3 w-14 mx-auto" />
                </TableHead>
                <TableHead className="px-4 py-3 text-center">
                  <Skeleton className="h-3 w-12 mx-auto" />
                </TableHead>
                <TableHead className="px-4 py-3">
                  <Skeleton className="h-3 w-14" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i} className="border-b border-border">
                  {/* Name Cell */}
                  <TableCell className="px-4 py-3">
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-36" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </TableCell>

                  {/* Self Chart Cell */}
                  <TableCell className="px-4 py-3 text-center">
                    <div className="flex justify-center">
                      <Skeleton className="h-16 w-20 rounded" />
                    </div>
                  </TableCell>

                  {/* Cognitive Cell */}
                  <TableCell className="px-4 py-3 text-center">
                    <Skeleton className="h-4 w-8 mx-auto" />
                  </TableCell>

                  {/* Pattern Cell */}
                  <TableCell className="px-4 py-3 text-center">
                    <Skeleton className="h-4 w-16 mx-auto" />
                  </TableCell>

                  {/* Status Cell */}
                  <TableCell className="px-4 py-3 text-center">
                    <div className="flex justify-center">
                      <Skeleton className="h-6 w-20 rounded-md" />
                    </div>
                  </TableCell>

                  {/* Actions Cell */}
                  <TableCell className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-start">
                      <Skeleton className="h-8 w-8 rounded" />
                      <Skeleton className="h-8 w-8 rounded" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Footer Skeleton */}
        <div className="border-t border-border px-4 py-3 bg-muted/20">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-16 rounded-md" />
              <Skeleton className="h-9 w-20 rounded-md" />
              <Skeleton className="h-9 w-20 rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CompactTableSkeleton = () => {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 p-4 border rounded-lg bg-card"
        >
          <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-full max-w-xs" />
            <Skeleton className="h-3 w-full max-w-sm" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-8 w-20 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
};
